/**
 * SSE over fetch.
 *
 * Not EventSource: EventSource can only issue GET requests with no body, and a
 * turn is a POST carrying the message, the site key and possibly a session
 * token. So we read the response body as a stream and parse the wire format
 * ourselves, which is about thirty lines and removes a whole class of
 * workarounds.
 */

/**
 * A turn that was refused before it began.
 *
 * 🔴 THE STATUS IS CARRIED BECAUSE ONE OF THEM IS RECOVERABLE AND THE REST ARE
 *    NOT. A 401 means the session token expired, which happens to every
 *    identified visitor 15 minutes after their page loaded, and the honest
 *    response is to get a new token and try again rather than to tell somebody
 *    their question failed. A bare `Error` cannot express that, so the caller
 *    used to render the server's own words instead: a real visitor on
 *    app.askverb.com was shown "Invalid session token" in red, mid-conversation.
 *
 * 🔴 AND THE DETAIL IS FOR THE CONSOLE, NEVER FOR THE PAGE. It is written for
 *    whoever installed the widget. The visitor gets a sentence chosen from the
 *    status by the caller, because "Invalid session token" tells them nothing,
 *    reads like a security problem, and appears on somebody else's website.
 */
export class SseHttpError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(detail || `Request failed with ${status}`);
    this.name = "SseHttpError";
  }
}

/**
 * A stream that STARTED and then stopped without saying it was done.
 *
 * 🔴 A DIFFERENT THING FROM NEVER CONNECTING, AND THE PANEL USED TO SAY THE
 *    SAME SENTENCE FOR BOTH. "That did not reach the assistant" is true when
 *    the request never got there. After a drop mid-answer it is false: the
 *    server has the question, usually finishes the answer and saves it, and
 *    the widget can fetch it (GET /v1/turn/:id/outcome). The caller recovers
 *    on this type and only on this type. docs/28 section 7b.
 */
export class SseDroppedError extends Error {
  constructor(readonly reason: "network" | "ended" | "silent") {
    super(`The stream dropped (${reason}).`);
    this.name = "SseDroppedError";
  }
}

/**
 * 🔴 SILENCE, NOT TOTAL TIME. The server sends a heartbeat every 15 seconds
 *    whatever else is happening, including while a confirmation card waits
 *    five minutes for a click, so three missed beats means the connection is
 *    dead even if the socket never says so. A phone switching from wifi to
 *    mobile data does exactly that: no error, no close, just nothing, and the
 *    panel said "Thinking" forever.
 */
const STREAM_SILENCE_MS = 45_000;

export interface SseMessage {
  readonly event: string;
  readonly data: unknown;
}

export interface SseOptions {
  readonly url: string;
  readonly body: unknown;
  readonly signal?: AbortSignal;
  readonly onMessage: (msg: SseMessage) => void;
  /** Tests only. See STREAM_SILENCE_MS. */
  readonly silenceMs?: number;
}

export async function streamSse(opts: SseOptions): Promise<void> {
  const res = await fetch(opts.url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(opts.body),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });

  if (!res.ok || !res.body) {
    const detail = await res.text().catch(() => "");
    throw new SseHttpError(res.status, readableError(detail));
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let sawDone = false;
  let silent = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const arm = (): void => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      silent = true;
      void reader.cancel().catch(() => {});
    }, opts.silenceMs ?? STREAM_SILENCE_MS);
  };
  arm();

  try {
    while (true) {
      let chunk: Awaited<ReturnType<typeof reader.read>>;
      try {
        chunk = await reader.read();
      } catch (err) {
        // The caller's abort is not a drop. Anything else here is the network.
        if (opts.signal?.aborted) throw err;
        throw new SseDroppedError(silent ? "silent" : "network");
      }
      const { done, value } = chunk;
      if (done) break;
      arm();

      buffer += decoder.decode(value, { stream: true });

      // Frames are separated by a blank line. Anything after the last blank line
      // is a partial frame and must stay in the buffer, splitting on every
      // newline instead is the classic way to lose half a JSON payload.
      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const parsed = parseFrame(frame);
        if (parsed?.event === "done") sawDone = true;
        if (parsed) opts.onMessage(parsed);
      }
    }
  } finally {
    clearTimeout(timer);
  }

  if (opts.signal?.aborted) return;
  if (silent) throw new SseDroppedError("silent");
  // The server always ends with `done`, even after an error event. A stream
  // that closed without it was cut: by a proxy, a deploy, or the network.
  if (!sawDone) throw new SseDroppedError("ended");
}

/**
 * A failed turn's body is `{"error":"..."}`, written for a person, same
 * convention as every admin route. Without this, that JSON's own braces and
 * quotes end up on screen as if they were the message: this is the same bug
 * the dashboard's `apiError` (lib/api.ts) already fixed once, on the other
 * side of the product.
 */
function readableError(detail: string): string {
  try {
    const parsed = JSON.parse(detail) as { error?: string };
    return typeof parsed.error === "string" && parsed.error ? parsed.error : detail;
  } catch {
    return detail;
  }
}

function parseFrame(frame: string): SseMessage | null {
  let event = "message";
  const dataLines: string[] = [];

  for (const line of frame.split("\n")) {
    // ": ping", the heartbeat that keeps intermediaries from dropping an idle
    // connection while a confirmation waits on a human.
    if (line.startsWith(":")) continue;
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }

  if (dataLines.length === 0) return null;
  const raw = dataLines.join("\n");
  try {
    return { event, data: JSON.parse(raw) };
  } catch {
    return { event, data: raw };
  }
}
